import type {
	ConceptDefinitionDTO as GeneratedConceptDefinitionDTO,
	ParameterizedConceptDTO as GeneratedParameterizedConceptDTO,
} from "@eu.mdotm.api/smart-workspaces";

/**
 * What the workspace area needs that `@eu.mdotm.api/smart-workspaces` does not give it directly:
 * the shapes the OpenAPI spec cannot describe, the label it does not carry, and the aliases that say
 * which role a value is playing where one schema serves two.
 *
 * Everything the client states plainly is imported from the client. A DTO does not earn a line here
 * for being a workspace DTO — only for one of the reasons above.
 */

/**
 * The field list every "build a parameterized concept to store" site restates — i.e. all of
 * `ParameterizedConceptDTO` except the four that are nobody's to copy over:
 *
 * - `uuid` is minted by whoever stores the concept, not by the translation;
 * - `breakdown` and `timeSeries` are **request-time** flags and must never be persisted. The pool is
 *   what "Create Column" copies out of and what the table's own request draws from, so a concept in it
 *   has to keep meaning "one number"; `withChartSeriesRequest` applies them per request instead;
 * - `dataProvider` is the key-metric threshold's *mode* rather than a property of the charted metric,
 *   and only the one site that builds the threshold concept sets it.
 *
 * Used as `satisfies PersistedConceptFields` on those literals, so a field added to the DTO cannot be
 * quietly left out of the concepts we store — which is exactly how `timeSeries` came to never be sent.
 */
export type PersistedConceptFields = Record<
	keyof Omit<GeneratedParameterizedConceptDTO, "uuid" | "breakdown" | "timeSeries" | "dataProvider">,
	unknown
>;

// --- Stacked column ------------------------------------------------------------------------------

/**
 * "Do not collapse this exposure concept to a single leaf". `parameters` keeps working as a *pin* and
 * composes with it: `parameters: {macroAssetClass: "Equity"}` + `breakdown: {dimension: "CURRENCY"}` =
 * currency exposure within equity. The two never name the same dimension — in the UI they are the same
 * choice, made on the level's own selector ("All" expands it, a value pins it).
 *
 * The backend reuses one schema for the request-time flag and for the chart's persisted configuration;
 * these two aliases say which role a given value is playing.
 */
export type {
	BreakdownRequestDTO as ConceptBreakdownDTO,
	BreakdownRequestDTO as ChartBreakdownPropertiesDTO,
} from "@eu.mdotm.api/smart-workspaces";

/** The label the backend uses for the aggregated tail when `othersBucket` is requested. */
export const breakdownOthersLabel = "Other";

/**
 * `dictionary[uuid].value` for a concept requested *with* a `breakdown`. Still local: `value` is
 * declared `type: object` in the spec, so the generated client cannot describe what is inside it.
 */
export type BreakdownValueDTO = {
	entries?: Array<{ label: string; value: number }>;
};

// --- Line (time series) --------------------------------------------------------------------------

/**
 * `dictionary[uuid].value` for a `TIME_SERIES` concept: one `[epochSeconds, value]` pair per instant,
 * chronological, already trimmed to the parameterized concept's `timeHorizon` by the backend.
 *
 * Still local for the same reason as {@link BreakdownValueDTO} — `value` is declared `type: object` in
 * the spec, so the generated client cannot describe what is inside it. Unlike the breakdown, though,
 * this shape *is* announced on the wire: the concept's `dataType` is `TIME_SERIES`, so a reader knows
 * which of the two to expect without looking at the request.
 *
 * Seconds, not milliseconds — the same convention as the portfolio-details performance chart
 * (`PerformanceSeriesData`). The chart layer multiplies; nothing else should.
 */
export type TimeSeriesValueDTO = Array<[timestampSec: number, value: number]>;

// --- Chart persistence ---------------------------------------------------------------------------

/**
 * The persisted chart type, used as the wire spells it — the workspace branches on these five values
 * directly and translates nothing, so there is no second vocabulary to keep in sync.
 *
 * One value needs reading with care: `HISTOGRAM` names the *column* chart (one bar per portfolio, one
 * metric), while `FREQUENCY` is the actual histogram. It is the legacy value, required and already
 * persisted in every workspace — the backend still writes it when it seeds a user's default workspace —
 * so renaming it on the wire would buy a data migration for nothing. `FREQUENCY` and `DENSITY` were
 * renamed there instead, which no workspace had persisted.
 *
 * What the user sees is a separate question, answered by `workspaceChartTypeLabels` in `./chart-type`:
 * the picker says "Column" for `HISTOGRAM` and "Line" for `PERFORMANCE_LINE`.
 */
export { SmartWorkspaceDTOChartTypeEnum as WorkspaceChartTypeEnum } from "@eu.mdotm.api/smart-workspaces";

// --- Frequency: POST /data-providers/distribution -------------------------------------------------

/**
 * The two roles of the same schema, named for the role. `ChartDistributionPropertiesDTO` is what the
 * caller *asks* for — `binCount` / `binWidth` are hints, never guarantees; `BinningDTO` is what the
 * backend actually *applied*, and its two numbers are required (zeroed when there was nothing to
 * bin). The echo can differ from the request under every mode, so axis labels come from it and from
 * `bins[].from` / `to`, never from the requested value.
 */
export type {
	ChartDistributionPropertiesDTO as DistributionBinningRequestDTO,
	BinningDTO as DistributionBinningEchoDTO,
} from "@eu.mdotm.api/smart-workspaces";

// --- Managed (system-shared) concepts ------------------------------------------------------------

/**
 * `true` when the concept is a system-shared (managed) concept: not deletable, configuration
 * read-only for the user. Column visibility/order and per-investment cell values stay editable.
 */
export function isManagedConcept(concept: GeneratedConceptDefinitionDTO | undefined): boolean {
	return concept?.managed === true;
}
